import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu as MenuIcon, X, Phone } from "lucide-react";
import { Logo } from "@/components/Logo";
import { SITE } from "@/data/site";
import { useOpenStatus } from "@/hooks/useOpenStatus";

const LINKS = [
  { to: "/", label: "Home", es: "Inicio" },
  { to: "/menu", label: "Menu", es: "Menú" },
  { to: "/visit", label: "Visit Us", es: "Visítanos" },
];

const StatusBar = () => {
  const { isOpen, label } = useOpenStatus();
  return (
    <div className="bg-palm text-cream" data-testid="hours-status-bar">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.18em] sm:px-8">
        <span className="flex items-center gap-2" data-testid="open-status">
          <span className={`h-2 w-2 rounded-full ${isOpen ? "bg-[#7EE2A8] animate-pulse" : "bg-mango"}`} />
          {label}
        </span>
        <span className="hidden sm:inline">Mon–Sat 10 AM – 6 PM · Sunday closed</span>
      </div>
    </div>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header className="fixed inset-x-0 top-0 z-50" data-testid="site-header">
      <StatusBar />
      <div className="border-b border-cream/10 bg-cocoa/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
          <Link to="/" data-testid="logo-home-link"><Logo /></Link>
          <nav className="hidden items-center gap-1 md:flex">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end
                data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
                className={({ isActive }) =>
                  `group relative rounded-full px-4 py-2 text-sm font-medium transition-colors ${isActive ? "text-mango" : "text-cream/85 hover:text-cream"}`
                }
              >
                {l.label}
                <span className="absolute inset-x-4 -bottom-0.5 h-px origin-left scale-x-0 bg-mango transition-transform duration-300 group-hover:scale-x-100" />
              </NavLink>
            ))}
            <a href={SITE.tel} data-testid="header-call-btn" className="ml-3 inline-flex items-center gap-2 rounded-full bg-hibiscus px-4 py-2 text-sm font-semibold text-cream transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-[#B83A22]">
              <Phone className="h-4 w-4" /> {SITE.phone}
            </a>
          </nav>
          <button onClick={() => setOpen((v) => !v)} className="grid h-11 w-11 place-items-center rounded-full border border-cream/20 text-cream md:hidden" data-testid="mobile-menu-toggle" aria-label="Toggle menu">
            {open ? <X className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
          </button>
        </div>
        {open && (
          <nav className="border-t border-cream/10 px-5 pb-6 pt-2 md:hidden" data-testid="mobile-nav">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end data-testid={`mobile-nav-${l.label.toLowerCase().replace(" ", "-")}`}
                className={({ isActive }) => `flex items-baseline justify-between border-b border-cream/10 py-4 font-display text-3xl ${isActive ? "text-mango" : "text-cream"}`}>
                {l.label}
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-cream/60">{l.es}</span>
              </NavLink>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};
